import React, { useState, useEffect } from "react"
import Card from "@mui/material/Card"
import Grid from "@mui/material/Grid"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDInput from "components/MDInput"
import MDButton from "components/MDButton"
import Button from "@mui/material/Button"
import { Checkbox, FormControlLabel, Icon } from "@mui/material"
import { useNavigate } from "react-router-dom"
import FacilityCard from "../FacilityCard"
import Paginator from "../../../Paginator"
import { ManageEstateData } from "../../../manage/estate"
import { ManageCatvData } from "pages/manage/catv"

export interface FacilityData {
  id: string
  name: string
  address: string
  phone: string
  building_type: number
  detection: number
  device_state: number
  no_device_count: number
  medical_records: number
  detection_log_dates: number[]
  device_state_log_dates: number[]
  log_dates: number[]
  create_date: number
  estate_id: string
  estate_name: string
  catv_id: string
  catv_name: string
  catv_logo: string
}

interface FacilityListProps {
  facilities: FacilityData[]
  catvs: ManageCatvData[]
  estates: ManageEstateData[]
  filter: number
}

function FacilityList({ facilities, catvs, estates, filter }: FacilityListProps): React.ReactElement {
  const navigate = useNavigate()

  const [searchText, setSearchText] = useState("")
  const [errorMessage, setErrorMessage] = useState("")
  const [searchStartDate, setSearchStartDate] = useState("")
  const [searchEndDate, setSearchEndDate] = useState("")
  const [showDetectionOnly, setShowDetectionOnly] = useState(false)
  const [showAlertOnly, setShowAlertOnly] = useState(false)

  const [filteredData, setFilteredData] = useState<FacilityData[]>([])
  const [selectCatv, setSelectCatv] = useState<string>("")
  const [selectEstate, setSelectEstate] = useState<string>("")
  const [currentPage, setCurrentPage] = useState(1)
  const [changeFilter, setChangeFilter] = useState<boolean>(false)
  const itemsPerPage = 15

  const paginatedData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

  // ソート処理関数
  const sortData = (data: FacilityData[]) => {
    return data.sort((a, b) => {
      // device_state 優先
      if (a.device_state !== 1 && b.device_state === 1) return 1
      if (a.device_state === 1 && b.device_state !== 1) return -1

      // device_state が同じ場合は device_state_log_dates が古いものを優先
      if (a.device_state !== 1 && b.device_state !== 1) {
        const aMinDeviceStateDate = Math.min(...(a.device_state_log_dates || [Number.MAX_SAFE_INTEGER]))
        const bMinDeviceStateDate = Math.min(...(b.device_state_log_dates || [Number.MAX_SAFE_INTEGER]))
        if (aMinDeviceStateDate !== bMinDeviceStateDate) {
          return aMinDeviceStateDate - bMinDeviceStateDate
        }
      }

      // detection 優先
      if (a.detection !== 1 && b.detection === 1) return 1
      if (a.detection === 1 && b.detection !== 1) return -1

      // detection が同じ場合は detection_log_dates が古いものを優先
      if (a.detection !== 1 && b.detection !== 1) {
        const aMinDetectionDate = Math.min(...(a.detection_log_dates || [Number.MAX_SAFE_INTEGER]))
        const bMinDetectionDate = Math.min(...(b.detection_log_dates || [Number.MAX_SAFE_INTEGER]))
        if (aMinDetectionDate !== bMinDetectionDate) {
          return aMinDetectionDate - bMinDetectionDate
        }
      }

      // 上記条件に該当しない場合は元の順番を維持（create_date順）
      return a.create_date - b.create_date
    })
  }

  // 検索処理
  const handleSearch = () => {
    const filtered = facilities.filter((facility) => {
      const matchesText =
        searchText === "" ||
        facility.name?.includes(searchText) ||
        facility.address?.includes(searchText) ||
        facility.phone?.includes(searchText)

      const startDate = searchStartDate ? new Date(searchStartDate + "T00:00:00+09:00").getTime() / 1000 : -Infinity
      const endDate = searchEndDate
        ? new Date(searchEndDate + "T00:00:00+09:00").setHours(23, 59, 59, 999) / 1000
        : Infinity

      let matchesDetectionLogDate = facility.log_dates?.some((logDate) => logDate >= startDate && logDate <= endDate)
      let matchesDeviceStateLogDate = facility.log_dates?.some((logDate) => logDate >= startDate && logDate <= endDate)

      if (startDate == -Infinity && endDate == Infinity) {
        matchesDetectionLogDate = true
        matchesDeviceStateLogDate = true
      }

      const matchesDate = matchesDetectionLogDate || matchesDeviceStateLogDate
      const matchesDetection = !showDetectionOnly || facility.detection !== 0
      const matchesAlert = !showAlertOnly || facility.device_state !== 0
      const matchesEstate = selectEstate == "" || selectEstate.split("|")[1] == facility.estate_id
      const matchesCatv = selectCatv == "" || selectCatv.split("|")[1] == facility.catv_id

      return matchesText && matchesDate && matchesDetection && matchesAlert && matchesEstate && matchesCatv
    })

    const sorted = sortData(filtered)
    setFilteredData(sorted)
    setCurrentPage(1)
    //console.log(sorted);
  }

  // 初期表示時にデータをフィルタリングしてソート
  useEffect(() => {
    const sorted = sortData([...facilities])
    setFilteredData(sorted)
  }, [facilities])

  // filter の値が変更されたときに検索条件を設定し、検索を実行
  useEffect(() => {
    if (filter === 1) {
      setShowDetectionOnly(false)
      setShowAlertOnly(false)
      setSearchText("")
      setSearchStartDate("")
      setSearchEndDate("")
    } else if (filter === 2) {
      setShowDetectionOnly(true)
      setShowAlertOnly(false)
      setSearchText("")
      setSearchStartDate("")
      setSearchEndDate("")
    } else if (filter === 3) {
      setShowDetectionOnly(false)
      setShowAlertOnly(true)
      setSearchText("")
      setSearchStartDate("")
      setSearchEndDate("")
    }
    setChangeFilter(true)
    handleSearch()
  }, [filter])

  //不動産事業者又はサービス提供元が変更された場合に検索を再実行する
  useEffect(() => {
    handleSearch()
  }, [selectEstate, selectCatv]) // filter が変更されたときに実行される

  // 検索条件が変更されたときに検索を再実行する
  useEffect(() => {
    if (changeFilter) {
      handleSearch()
      setChangeFilter(false)
    }
  }, [searchText, searchStartDate, searchEndDate, showDetectionOnly, showAlertOnly, facilities])

  const validate = (message: string) => {
    setSearchText(message)

    if (message.length > 8) {
      setErrorMessage("入力文字数は8文字以内です")
    } else {
      setErrorMessage("") // エラーがない場合はクリア
    }
  }

  const totalPages = Math.ceil(filteredData.length / itemsPerPage)

  return (
    <Card sx={{ height: "100%", paddingBottom: "10px" }}>
      <MDBox pt={3} px={3} pb={2}>
        <MDTypography variant="h4" fontWeight="medium">
          施設リスト
        </MDTypography>
        {catvs.length > 0 && (
          <>
            <MDBox display="flex" alignItems="center" mt={1} mb={1} gap={1}>
              <span style={{ fontSize: "18px", fontWeight: "bold", color: "#344767" }}>サービス提供元</span>
            </MDBox>
            <MDBox display="flex" flexWrap="wrap" alignItems="left" mb={3}>
              <Grid key={-1} item xs={6} md={4} lg={3} xl={3} mb={1}>
                <Button
                  variant="contained"
                  color={selectCatv == "" ? "primary" : "info"}
                  size="small"
                  sx={{ color: selectCatv == "" ? "#FFF" : "#555", fontWeight: "normal", width: "calc(100% - 10px)" }}
                  onClick={() => {
                    setSelectCatv("")
                  }}
                >
                  全て
                </Button>
              </Grid>
              {catvs.map((catv) => (
                <Grid key={-1} item xs={6} md={4} lg={3} xl={3} mb={1}>
                  <Button
                    variant="contained"
                    color={selectCatv == catv.id ? "primary" : "info"}
                    size="small"
                    sx={{
                      color: selectCatv == catv.id ? "#FFF" : "#555",
                      fontWeight: "normal",
                      width: "calc(100% - 10px)"
                    }}
                    onClick={() => {
                      setSelectCatv(catv.id)
                    }}
                  >
                    {catv.name}
                  </Button>
                </Grid>
              ))}
            </MDBox>
          </>
        )}
        {estates.length > 0 && (
          <>
            <MDBox display="flex" alignItems="center" mt={1} mb={1} gap={1}>
              <span style={{ fontSize: "18px", fontWeight: "bold", color: "#344767" }}>不動産業者</span>
            </MDBox>
            <MDBox display="flex" flexWrap="wrap" alignItems="left" mb={3}>
              <Grid key={-1} item xs={6} md={4} lg={3} xl={3} mb={1}>
                <Button
                  variant="contained"
                  color={selectEstate == "" ? "primary" : "info"}
                  size="small"
                  sx={{ color: selectEstate == "" ? "#FFF" : "#555", fontWeight: "normal", width: "calc(100% - 10px)" }}
                  onClick={() => {
                    setSelectEstate("")
                  }}
                >
                  全て
                </Button>
              </Grid>
              {estates.map((estate) => (
                <Grid key={-1} item xs={6} md={4} lg={3} xl={3} mb={1}>
                  <Button
                    variant="contained"
                    color={selectEstate == estate.id ? "primary" : "info"}
                    size="small"
                    sx={{
                      color: selectEstate == estate.id ? "#FFF" : "#555",
                      fontWeight: "normal",
                      width: "calc(100% - 10px)"
                    }}
                    onClick={() => {
                      setSelectEstate(estate.id)
                    }}
                  >
                    {estate.name}
                  </Button>
                </Grid>
              ))}
            </MDBox>
          </>
        )}
        <MDBox display="flex" alignItems="center" sx={{ fontSize: "0.875rem" }} gap={1}>
          <MDBox
            width="20px"
            height="20px"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            bgColor="primary"
            variant="gradient"
          />
          機器エラー
          <MDBox
            width="20px"
            height="20px"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            bgColor="warning"
            variant="gradient"
            ml={1}
          />
          不在検知
          <MDBox
            width="20px"
            height="20px"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            bgColor="info"
            variant="gradient"
            ml={1}
          />
          機器未登録
          <MDBox
            width="20px"
            height="20px"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            bgColor="white"
            variant="contained"
            ml={1}
            sx={{ border: "1px solid #ddd" }}
          />
          正常
        </MDBox>
        <MDBox display="flex" flexWrap="wrap" alignItems="left" mt={3} mb={3} gap={3}>
          <MDBox display="flex" alignItems="center" gap={1}>
            <div style={{ position: "relative", display: "inline-block" }}>
              <MDInput
                label="施設名"
                value={searchText}
                onChange={(e) => validate(e.target.value)}
                sx={{ width: "160px" }}
                error={Boolean(errorMessage)} // エラー時のスタイル変更
              />
              <div
                style={{
                  position: "absolute",
                  top: "100%", // 入力欄の下に配置
                  left: 0,
                  width: "100%",
                  fontSize: "12px",
                  color: "red",
                  marginTop: "4px",
                  whiteSpace: "nowrap"
                }}
              >
                {errorMessage}
              </div>
            </div>
          </MDBox>
          <MDInput
            type="date"
            value={searchStartDate}
            onChange={(e) => setSearchStartDate(e.target.value)}
            sx={{ width: "160px" }}
          />
          〜
          <MDInput
            type="date"
            value={searchEndDate}
            onChange={(e) => setSearchEndDate(e.target.value)}
            sx={{ width: "160px" }}
          />
          <MDBox display="flex" alignItems="center" gap={1}>
            <FormControlLabel
              control={
                <Checkbox checked={showDetectionOnly} onChange={(e) => setShowDetectionOnly(e.target.checked)} />
              }
              label="不在検知"
            />
            <FormControlLabel
              control={<Checkbox checked={showAlertOnly} onChange={(e) => setShowAlertOnly(e.target.checked)} />}
              label="機器エラー"
            />
          </MDBox>
          <MDButton
            variant="gradient"
            color="info"
            size="medium"
            onClick={handleSearch}
            disabled={errorMessage !== ""} // エラーメッセージがある場合は無効化
          >
            <Icon>search</Icon>&nbsp; 検索
          </MDButton>
        </MDBox>
        <MDBox p={2}>
          <Grid container spacing={3}>
            {paginatedData.map((facility, index) => (
              <Grid key={index} item xs={12} md={6} lg={4} xl={4}>
                <FacilityCard
                  icon={facility.building_type === 0 ? "domain" : "house"}
                  title={facility.name}
                  iconAlert={facility.device_state > 0}
                  iconDetection={facility.detection > 0}
                  iconNoDevice={facility.no_device_count > 0}
                  catv_logo={facility.catv_logo}
                  catv_name={facility.catv_name}
                  value={`不在検知:${facility.detection}件 機器エラー:${facility.device_state}件`}
                  onClick={() => {
                    navigate(`/facility/${facility.id.split("|")[1]}`)
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </MDBox>
      </MDBox>
      {/* ページャー */}
      <Paginator
        key={"FacilityTileListPaginator"}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </Card>
  )
}

export default FacilityList
